import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { ARENAS, BATTLE_DURATIONS_SEC, DEFAULT_BATTLE_SEC, splitPot, usdt, formatUsdt, type BattleSec } from "@degenduel/shared";
import { api, ApiError, connectLive, credentials, playerId } from "./api.js";
import { moneyChanged, type Landed } from "./money.js";
import { prefetchHistory } from "./history.js";
import { go } from "./router.js";
import { play } from "./sound.js";
import { tg } from "./telegram.js";
import { askToMessage, fetchRival, type Challenge } from "./challenge.js";

export type Side = "long" | "short";

export interface Me {
  user: { id: string; username: string | null; firstName: string };
  balance: { available: string; inBattle: string; withdrawing: string };
  battles: number;
  stakeLimit: string;
  xp?: number;
  level?: number;
  /** Wins in a row now, and the best ever. */
  streak?: number;
  bestStreak?: number;
  /** Finished the intro on any device. */
  onboarded: boolean;
  serverTime: number;
}

export interface ArenaStatus {
  id: string;
  name: string;
  pair: string;
  open: boolean;
  /** Coming soon: live price shown, not playable yet. `next` is the one the unlock bar is filling. */
  soon?: { next: boolean; progress: number };
}

export interface Battle {
  id: string;
  arena: string;
  stake: string;
  long: string;
  short: string;
  state: "live" | "settling" | "settled" | "cancelling" | "cancelled";
  startPrice: string | null;
  endPrice: string | null;
  startedAt: number | null;
  endsAt: number | null;
  /** 180 for a 3:00 battle, 60 for Blitz. */
  durationSec: number;
  outcome: "long" | "short" | "draw" | "refund" | null;
  cancelReason: string | null;
  /** The fee the winner paid: 400 is 4%, 200 a power hour's 2%, 0 an invited friend's fee-free win. */
  feeBps?: number;
  /** Which of an invited friend's 5 fee-free wins this was. */
  feeFreeWin?: number | null;
}

/** The daily power hour: running now, or the next one. Server time. */
export interface PowerHour {
  startsAt: number;
  endsAt: number;
  feePct: number;
}

/** A search in progress, as the server holds it. */
interface SearchTicket {
  arena: string;
  side: Side;
  stake: string;
  custom: boolean;
  durationSec: BattleSec;
  /** Server time. */
  joinedAt: number;
}

type Active = { battle: Battle | null; ticket: SearchTicket | null; latest?: Battle | null };

const RESULT_KEY = "dd.lastResult";

/** The last battle whose result this phone has shown, so a missed one is shown once and only once. */
function resultShown(): string | null {
  try {
    return localStorage.getItem(RESULT_KEY);
  } catch {
    return null;
  }
}

function markResultShown(id: string): void {
  try {
    localStorage.setItem(RESULT_KEY, id);
  } catch {
    // Private mode: a missed result may show again after a restart, which does no harm.
  }
}

const DEVICE_KEY = "dd.device";
let sessionDevice: string | null = null;

/** A random id kept on this phone, so two accounts on one phone are never matched against each other. */
function deviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) localStorage.setItem(DEVICE_KEY, (id = crypto.randomUUID()));
    return id;
  } catch {
    // Storage blocked: the id lasts until the app closes, which still covers one sitting.
    return (sessionDevice ??= crypto.randomUUID());
  }
}

export interface Tick {
  p: number;
  t: number;
}

interface Selection {
  arena: string;
  stake: string;
  custom: boolean;
  durationSec: BattleSec;
}

const LENGTH_KEY = "dd.battleSec";

/** The battle length the player picked last time; 3:00 until they pick Blitz. */
function savedLength(): BattleSec {
  try {
    const v = Number(localStorage.getItem(LENGTH_KEY));
    return (BATTLE_DURATIONS_SEC as readonly number[]).includes(v) ? (v as BattleSec) : DEFAULT_BATTLE_SEC;
  } catch {
    return DEFAULT_BATTLE_SEC;
  }
}

interface Game {
  me: Me | null;
  arenas: ArenaStatus[];
  powerHour: PowerHour | null;
  /** The admin switch for the lobby's live feed. */
  liveFeed: boolean;
  /** The fee a battle starting now would pay: 2% in the power hour, 4% the rest of the day. The server decides the real one. */
  feeNow(): bigint;
  /** The last 3 minutes of prices per arena (up to 10 a second), newest last. */
  prices: Record<string, Tick[]>;
  selection: Selection;
  queued: Side | null;
  /** When this player joined the queue, and how many were waiting per side at that moment. */
  queuedAt: number | null;
  queueDepth: { long: number; short: number } | null;
  /** The "no opponent yet" screen was shown for this queue ticket. */
  nudged: boolean;
  markNudged(): void;
  battle: Battle | null;
  mySide: Side | null;
  /** Server time now, corrected for the phone's clock, for countdowns. */
  now(): number;
  error: string | null;
  select(patch: Partial<Selection>): void;
  /** `change` is a new choice made in the same tap (screen 50's options): the search uses it, not the old one. */
  /** `from` is the screen the side was picked on: a failed join goes back there, and so does a cancelled search. */
  findOpponent(side: Side, change?: Partial<Selection>, from?: SearchFrom): Promise<void>;
  /** False when a match got there first: the player is in that battle now, not back in the lobby. */
  leaveQueue(): Promise<boolean>;
  topUp(): Promise<void>;
  refresh(): Promise<void>;
  clearError(): void;
  dismissBattle(): void;
  /** This player's own challenge or rematch offer: open, or the last one to close, so its screen can say how. */
  challenge: Challenge | null;
  /** Rematch offers waiting for this player. */
  incoming: Challenge[];
  /** Makes a link challenge with the current selection; the stake locks now. */
  createChallenge(side: Side): Promise<Challenge>;
  /** Calls off this player's open challenge or rematch offer. Accepted first means a battle: the app goes into it. */
  cancelChallenge(): Promise<void>;
  /** Takes a challenge or a rematch offer: the battle starts and the app goes into it. */
  acceptChallenge(id: string): Promise<void>;
  /** Offers the last opponent a rematch, or takes theirs if they asked first. */
  rematch(battleId: string): Promise<void>;
  declineRematch(id: string): Promise<void>;
  forgetChallenge(): void;
  /** Asks the server again for this player's challenges, in case the live socket missed a change. */
  checkChallenges(): void;
}

const GameContext = createContext<Game | null>(null);

export type SearchFrom = "App10" | "App76" | "App10w";
const SEARCH_FROM = "dd:searchFrom";
/** Where a cancelled search goes: back to the chart it was started from, or else the lobby. */
export function searchCancelledTo(): string {
  return sessionStorage.getItem(SEARCH_FROM) === "App76" ? "App76" : "App09";
}

export function useGame(): Game {
  const g = useContext(GameContext);
  if (!g) throw new Error("useGame outside GameProvider");
  return g;
}

/** The pot, fee and victory payout for a stake, from the shared money rules. */
export function payoutFor(stake: string, feeBps: bigint | number = 400n) {
  const s = splitPot(usdt(stake), { feeBps: BigInt(feeBps) });
  return { pot: formatUsdt(s.pot), fee: formatUsdt(s.fee), payout: formatUsdt(s.winnerPayout) };
}

/** The last 3 minutes of prices: a whole battle fits on the chart. Prices arrive up to 10 a second. */
const HISTORY_MS = 180_000;
const HISTORY_MAX = 1_800;
function keepRecent(ticks: Tick[]): Tick[] {
  const cutoff = (ticks.at(-1)?.t ?? 0) - HISTORY_MS;
  const recent = ticks.filter((x) => x.t >= cutoff);
  return recent.length > HISTORY_MAX ? recent.slice(-HISTORY_MAX) : recent;
}
/** Battle states that still change on their own; the app keeps checking until the battle leaves them. */
const OPEN = new Set(["live", "settling", "cancelling"]);
/** A battle that started this recently is a fresh match: it gets the quick MATCHED flash first. */
const VERSUS_MS = 3_000;

export function GameProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [arenas, setArenas] = useState<ArenaStatus[]>(ARENAS.map((a) => ({ ...a, open: true })));
  const [powerHour, setPowerHour] = useState<PowerHour | null>(null);
  const [liveFeed, setLiveFeed] = useState(false);
  const [prices, setPrices] = useState<Record<string, Tick[]>>({});
  const [selection, setSelection] = useState<Selection>(() => ({ arena: "BTC", stake: "2", custom: false, durationSec: savedLength() }));
  const [queued, setQueued] = useState<Side | null>(null);
  const joining = useRef<Promise<unknown> | null>(null);
  const joinEpoch = useRef(0);
  const [queuedAt, setQueuedAt] = useState<number | null>(null);
  const [queueDepth, setQueueDepth] = useState<{ long: number; short: number } | null>(null);
  const [nudged, setNudged] = useState(false);
  const [battle, setBattle] = useState<Battle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const meRef = useRef<Me | null>(null);
  const offset = useRef(0);
  meRef.current = me;
  const queuedRef = useRef<Side | null>(null);
  queuedRef.current = queued;
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [incoming, setIncoming] = useState<Challenge[]>([]);
  const challengeRef = useRef<Challenge | null>(null);
  challengeRef.current = challenge;

  // Every change to a challenge this player made or was offered: from the live socket, a check, or their own tap.
  const onChallenge = useCallback((c: Challenge) => {
    const myId = playerId();
    if (c.creator.id === myId) {
      // An older answer never overwrites a newer state of the same challenge.
      setChallenge((prev) => (prev?.id === c.id && prev.state !== "open" && c.state === "open" ? prev : { ...prev, ...c, link: c.link ?? (prev?.id === c.id ? prev.link : undefined), url: c.url ?? (prev?.id === c.id ? prev.url : undefined) }));
    }
    if (c.target === myId) {
      setIncoming((list) => (c.state === "open" ? [...list.filter((x) => x.id !== c.id), c] : list.filter((x) => x.id !== c.id)));
    }
  }, []);

  const loadChallenges = useCallback(async () => {
    const m = await api<{ open: Challenge | null; incoming: Challenge[] }>("/v1/challenges");
    if (m.open) onChallenge(m.open);
    else if (challengeRef.current?.state === "open") {
      // It closed while the app was away: ask how, so its screen can say.
      const r = await api<{ challenge: Challenge }>(`/v1/challenges/${challengeRef.current.id}`).catch(() => null);
      if (r) onChallenge(r.challenge);
    }
    setIncoming(m.incoming);
  }, [onChallenge]);

  // Picks up a search the server is still running: the app was closed or reloaded mid-search, or a
  // second tap found one already going. The player lands back on the search screen, not a new one.
  const resume = useCallback((t: SearchTicket) => {
    const wasSearching = queuedRef.current !== null;
    flushSync(() => {
      setSelection({ arena: t.arena, stake: t.stake, custom: t.custom, durationSec: t.durationSec });
      setQueued(t.side);
      setQueuedAt(t.joinedAt - offset.current);
      if (!wasSearching) {
        setNudged(false);
        setQueueDepth(null);
      }
    });
    if (!wasSearching) go("App11", true);
  }, []);

  // The search ended without a match (time limit): back to the lobby, which says the stake is back.
  const searchEnded = useCallback(() => {
    if (!queuedRef.current) return;
    flushSync(() => {
      setQueued(null);
      setQueuedAt(null);
      setError("search_ended");
    });
    go("App09", true);
  }, []);

  const refresh = useCallback(async () => {
    if (!credentials()) return;
    const sent = Date.now();
    const [m, s, active] = await Promise.all([
      api<Me>("/v1/me").then((m) => ({ m, rtt: Date.now() - sent })),
      api<{ arenas: ArenaStatus[]; powerHour?: PowerHour | null; liveFeed?: boolean }>("/v1/status"),
      api<Active>("/v1/battles/active?latest=1"),
    ]).then(([me, s, a]) => {
      // The server's clock was read about halfway through the round trip.
      offset.current = me.m.serverTime + me.rtt / 2 - (sent + me.rtt);
      return [me.m, s, a] as const;
    });
    setMe(m);
    setArenas(s.arenas);
    setPowerHour(s.powerHour ?? null);
    setLiveFeed(!!s.liveFeed);
    if (active.battle) setBattle(active.battle);
    else if (active.ticket && !joining.current) resume(active.ticket);
    // A battle that finished while the app was closed or in the background: its result screen,
    // once. Never in the middle of a new search.
    else if (active.latest && active.latest.id !== resultShown() && !queuedRef.current && !joining.current) {
      onBattleRef.current(active.latest);
    }
    // Runs at start, after every battle and on coming back to the app: the moments history can change.
    prefetchHistory();
    void loadChallenges().catch(() => {});
  }, [resume, loadChallenges]);

  const onBattleRef = useRef<(b: Battle) => void>(() => {});
  const lastSeen = useRef<string>("");
  const onBattle = useCallback((b: Battle) => {
    // The live socket and the backup check both report battles; only a real change moves screens.
    const key = `${b.id}:${b.state}`;
    const moved = lastSeen.current !== key;
    lastSeen.current = key;
    // Commit the battle before switching screens, so the next screen never draws with stale state.
    flushSync(() => {
      setBattle(b);
      setQueued(null);
    });
    if (b.state === "settled" || b.state === "cancelled") markResultShown(b.id);
    if (!moved) return;
    // From Telegram's login data, not the profile: a result can arrive before the profile loads.
    const myId = playerId();
    const side: Side | null = b.long === myId ? "long" : b.short === myId ? "short" : null;
    if (side) void fetchRival(side === "long" ? b.short : b.long);
    if (b.state === "live") {
      // A fresh match flashes the Versus screen first; rejoining a battle already under way goes straight in.
      const fresh = b.startedAt !== null && Date.now() + offset.current - b.startedAt < VERSUS_MS;
      go(fresh ? "App12" : "App14", true);
    }
    else if (b.state === "settled") {
      if (b.outcome !== "refund") play(b.outcome === "draw" ? "draw" : b.outcome === side ? "win" : "lose");
      go(b.outcome === "refund" ? "App52" : b.outcome === "draw" ? "App19" : b.outcome === side ? "App17" : "App18", true);
      void refresh();
    } else if (b.state === "cancelled") {
      go("App52", true); // the only way a battle is cancelled now: no confirmed start price
      void refresh();
    }
  }, [refresh]);
  onBattleRef.current = onBattle;

  // The first load retries until it works (weak mobile data), and the app reloads its state whenever
  // it comes back to the front.
  useEffect(() => {
    let stop = false;
    let wait = 1_000;
    const load = () =>
      void refresh().catch(() => {
        if (stop) return;
        setTimeout(load, wait);
        wait = Math.min(wait * 2, 10_000);
      });
    load();
    const onFront = () => document.visibilityState === "visible" && void refresh().catch(() => {});
    document.addEventListener("visibilitychange", onFront);
    tg?.onEvent?.("activated", onFront);
    return () => {
      stop = true;
      document.removeEventListener("visibilitychange", onFront);
      tg?.offEvent?.("activated", onFront);
    };
  }, [refresh]);

  // Live prices for every arena plus this player's own battle events, over one socket.
  // Opens as soon as the app starts, not after the profile loads, so prices show at once.
  const myId = playerId();
  useEffect(() => {
    if (!myId) return;
    const channels = [...ARENAS.map((a) => `price:${a.id}`), `player:${myId}`, "feed"];
    // Ticks for 5 arenas arrive 20 times a second; collect them and redraw at most 4 times a second,
    // so cheap phones stay smooth.
    let pending: Record<string, Tick[]> = {};
    const flush = setInterval(() => {
      const batch = pending;
      pending = {};
      if (!Object.keys(batch).length) return;
      setPrices((prev) => {
        const next = { ...prev };
        for (const [arena, ticks] of Object.entries(batch)) next[arena] = keepRecent([...(prev[arena] ?? []), ...ticks]);
        return next;
      });
    }, 250);
    const stop = connectLive((ch, data) => {
      if (ch === "feed") {
        // A new win for the live feed: its components listen for this.
        dispatchEvent(new CustomEvent("dd:feed", { detail: data }));
        return;
      }
      if (ch.startsWith("price:")) {
        const m = data as { p?: string | null; t?: number; h?: [string, number][] };
        const arena = ch.slice(6);
        if (m.h) {
          // The last 3 minutes, sent once on subscribe: fills cards and charts immediately.
          const history = m.h.map(([p, t]) => ({ p: Number(p), t }));
          setPrices((prev) => ({ ...prev, [arena]: keepRecent([...history, ...(prev[arena] ?? []).filter((x) => x.t > history.at(-1)!.t)]) }));
          return;
        }
        if (!m.p || !m.t) return;
        (pending[arena] ??= []).push({ p: Number(m.p), t: m.t });
      } else {
        const ev = data as { type: string; battle?: Battle };
        if (ev.type === "deposit") {
          moneyChanged({ ...(data as Omit<Landed, "at">), at: Date.now() });
          void refresh().catch(() => {});
        } else if (ev.type === "search_ended") {
          searchEnded();
          void refresh().catch(() => {});
        } else if (ev.type === "challenge") {
          onChallenge((data as { challenge: Challenge }).challenge);
        } else if (ev.battle) onBattle(ev.battle);
      }
    }, channels);
    return () => {
      clearInterval(flush);
      stop();
    };
  }, [myId, onBattle, onChallenge, searchEnded, refresh]);

  // A challenge waiting for a taker: the socket brings the answer, and a check every 3 seconds catches one it missed.
  const openId = challenge?.state === "open" ? challenge.id : null;
  useEffect(() => {
    if (!openId) return;
    const t = setInterval(() => {
      void api<{ challenge: Challenge }>(`/v1/challenges/${openId}`)
        .then(async ({ challenge: c }) => {
          onChallenge(c);
          if (c.battleId) await api<Battle>(`/v1/battles/${c.battleId}`).then(onBattle);
        })
        .catch(() => {});
    }, 3_000);
    return () => clearInterval(t);
  }, [openId, onChallenge, onBattle]);

  useEffect(() => {
    const open = battle && OPEN.has(battle.state);
    if (!queued && !open) return;
    const t = setInterval(() => {
      if (open) {
        void api<Battle>(`/v1/battles/${battle!.id}`).then(onBattle).catch(() => {});
        return;
      }
      const asked = joinEpoch.current;
      void api<Active>("/v1/battles/active")
        .then((a) => {
          if (a.battle) onBattle(a.battle);
          // An answer to a check sent while a join was on its way says nothing about the search now.
          else if (!a.ticket && !joining.current && asked === joinEpoch.current) {
            searchEnded();
            void refresh();
          }
        })
        .catch(() => {});
    }, 3_000);
    return () => clearInterval(t);
  }, [queued, battle?.id, battle?.state, onBattle, refresh, searchEnded]);

  const game = useMemo<Game>(() => {
    const myId = playerId();
    const mySide: Side | null = battle ? (battle.long === myId ? "long" : battle.short === myId ? "short" : null) : null;
    const nowMs = () => Date.now() + offset.current;
    return {
      me,
      arenas,
      powerHour,
      liveFeed,
      feeNow: () => (powerHour && nowMs() >= powerHour.startsAt && nowMs() < powerHour.endsAt ? 200n : 400n),
      prices,
      selection,
      queued,
      queuedAt,
      queueDepth,
      nudged,
      markNudged: () => setNudged(true),
      battle,
      mySide,
      now: () => Date.now() + offset.current,
      error,
      select: (patch) => {
        if (patch.durationSec) {
          try {
            localStorage.setItem(LENGTH_KEY, String(patch.durationSec));
          } catch {
            // Private mode: the choice lasts until the app closes.
          }
        }
        setSelection((s) => ({ ...s, ...patch }));
      },
      async findOpponent(side, change = {}, from = "App10") {
        setError(null);
        sessionStorage.setItem(SEARCH_FROM, from);
        const pick = { ...selection, ...change };
        // Not enough to cover the stake: no search at all. The lobby already offers a top-up instead.
        if (!me || usdt(me.balance.available) < usdt(pick.stake)) {
          setError("low_balance");
          tg?.HapticFeedback?.notificationOccurred("error");
          throw new Error("low_balance");
        }
        // The search screen shows at once; the request catches up behind it.
        flushSync(() => {
          setBattle(null);
          setQueued(side);
          setQueuedAt(Date.now());
          setNudged(false);
          setQueueDepth(null);
        });
        go("App11", true);
        const join = api<{ queued: boolean; battle?: Battle; depth?: { long: number; short: number } }>("/v1/queue", {
          method: "POST",
          body: { ...pick, side, device: deviceId() },
        });
        joining.current = join;
        joinEpoch.current++;
        try {
          const r = await join;
          if (r.battle) onBattle(r.battle);
          else setQueueDepth(r.depth ?? null);
        } catch (e) {
          // The answer was lost, but the join may have landed: ask before giving up on the search.
          // The answer was lost, or a search was already running (the app was closed mid-search):
          // either way the server knows, so carry on with whatever it has.
          if (e instanceof ApiError && (e.code === "network" || e.code === "already_queued" || e.code === "already_in_battle")) {
            const a = await api<Active>("/v1/battles/active").catch(() => null);
            if (a?.battle) return onBattle(a.battle);
            if (a?.ticket) return resume(a.ticket);
          }
          flushSync(() => {
            setQueued(null);
            setQueuedAt(null);
          });
          setError(e instanceof ApiError ? e.code : "network");
          if (e instanceof ApiError && e.code === "join_channel") go(e.detail.left ? "App73" : "App72", true);
          else go(from, true);
          throw e;
        } finally {
          if (joining.current === join) joining.current = null;
          joinEpoch.current++;
        }
      },
      async leaveQueue() {
        // Cancel tapped before the join reached the server: let it land first, or the cancel misses it.
        await joining.current?.catch(() => {});
        const r = await api<{ left: boolean }>("/v1/queue", { method: "DELETE" });
        if (!r.left) {
          // Matched before the cancel arrived: matched means you play, so take them into the battle.
          const a = await api<{ battle: Battle | null }>("/v1/battles/active").catch(() => null);
          if (a?.battle) {
            onBattle(a.battle);
            return false;
          }
        }
        flushSync(() => {
          setQueued(null);
          setQueuedAt(null);
        });
        return true;
      },
      async topUp() {
        try {
          await api("/v1/play-money", { method: "POST" });
          await refresh();
        } catch (e) {
          // Real money: no free top-ups, so topping up means a deposit.
          if (e instanceof ApiError && e.status === 404) go("App39");
          else throw e;
        }
      },
      refresh,
      clearError: () => setError(null),
      dismissBattle: () => setBattle(null),
      challenge,
      incoming,
      async createChallenge(side) {
        askToMessage();
        const r = await api<{ challenge: Challenge }>("/v1/challenges", {
          method: "POST",
          body: { arena: selection.arena, side, stake: selection.stake, durationSec: selection.durationSec },
        });
        onChallenge(r.challenge);
        void refresh().catch(() => {});
        return r.challenge;
      },
      async cancelChallenge() {
        const c = challengeRef.current;
        if (!c || c.state !== "open") return;
        const r = await api<{ cancelled: boolean; challenge: Challenge }>(`/v1/challenges/${c.id}/cancel`, { method: "POST" });
        onChallenge(r.challenge);
        // Taken a moment before the cancel landed: taken means you play.
        if (!r.cancelled && r.challenge.battleId) await api<Battle>(`/v1/battles/${r.challenge.battleId}`).then(onBattle);
        void refresh().catch(() => {});
      },
      async acceptChallenge(id) {
        const r = await api<{ battle: Battle; challenge: Challenge }>(`/v1/challenges/${id}/accept`, { method: "POST" });
        setIncoming((list) => list.filter((x) => x.id !== id));
        onBattle(r.battle);
      },
      async rematch(battleId) {
        const r = await api<{ challenge: Challenge; battle: Battle | null }>("/v1/rematch", { method: "POST", body: { battleId } });
        if (r.battle) {
          setIncoming((list) => list.filter((x) => x.fromBattle !== battleId));
          onBattle(r.battle);
        } else onChallenge(r.challenge);
      },
      async declineRematch(id) {
        setIncoming((list) => list.filter((x) => x.id !== id));
        await api(`/v1/challenges/${id}/decline`, { method: "POST" }).catch(() => {});
      },
      forgetChallenge: () => setChallenge(null),
      checkChallenges: () => void loadChallenges().catch(() => {}),
    };
  }, [me, arenas, powerHour, liveFeed, prices, selection, queued, queuedAt, queueDepth, nudged, battle, error, onBattle, refresh, challenge, incoming, onChallenge, loadChallenges]);

  return <GameContext.Provider value={game}>{children}</GameContext.Provider>;
}

/** Two letters for an avatar: from the @handle, else the first name. */
export function initials(me: Me | null | undefined, fallback = "DD"): string {
  const name = me?.user.username ?? me?.user.firstName ?? fallback;
  return name.replace(/^@/, "").slice(0, 2).toUpperCase();
}

export interface PlayerCard {
  id: string;
  username: string | null;
  firstName: string;
  battles: number;
  wins: number;
  level?: number;
  streak?: number;
}

const cards = new Map<string, Promise<PlayerCard | null>>();

/** A player's public card (name, record), fetched once and cached for the session. */
export function usePlayer(id: string | null | undefined): PlayerCard | null {
  const [card, setCard] = useState<PlayerCard | null>(null);
  useEffect(() => {
    if (!id) return;
    if (!cards.has(id)) cards.set(id, api<PlayerCard>(`/v1/players/${id}`).catch(() => null));
    void cards.get(id)!.then(setCard);
  }, [id]);
  return card;
}

export function displayName(card: PlayerCard | null, fallback = "player"): string {
  return card ? (card.username ? `@${card.username}` : card.firstName) : fallback;
}

export function cardInitials(card: PlayerCard | null): string {
  return (card?.username ?? card?.firstName ?? "??").slice(0, 2).toUpperCase();
}

/** "LV 12 · 61% WINS · x4 STREAK", or "LV 1 · NEW PLAYER" before the first result. The streak shows from x2. */
export function record(card: PlayerCard | null): string {
  if (!card) return "NEW PLAYER";
  const lv = `LV ${card.level ?? 1}`;
  if (card.battles === 0) return `${lv} · NEW PLAYER`;
  const streak = (card.streak ?? 0) >= 2 ? ` · x${card.streak} STREAK` : "";
  return `${lv} · ${Math.round((card.wins / card.battles) * 100)}% WINS${streak}`;
}

/** Re-renders every interval ms, for countdowns. */
export function useTicker(ms = 250): void {
  const [, set] = useState(0);
  useEffect(() => {
    const t = setInterval(() => set((n) => n + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}
